export type AnnouncementCategory = 'NewFeature' | 'Improvement' | 'Fix';
export type AnnouncementStatus = 'Draft' | 'Published';
export type AnnouncementState = 'Draft' | 'Scheduled' | 'Published';
export type AnnouncementVote = 'Yes' | 'No';

export interface AnnouncementStep {
  text: string;
  imageUrl: string;
}

export interface Announcement {
  id?: number;
  title: string;
  summary: string;
  category: AnnouncementCategory;
  version: string;
  status?: AnnouncementStatus;
  state?: AnnouncementState;
  publishDate: string;
  coverImageUrl: string;
  whatChanged: string;
  whereToFind: string;
  steps: AnnouncementStep[];
  notes: string;
  targetPath: string;
  targetButtonText: string;
}

export interface AnnouncementRow {
  id: number;
  title: string;
  category: AnnouncementCategory;
  version: string;
  status: AnnouncementStatus;
  publishDate: string;
  state: AnnouncementState;
  updatedAt: string;
  feedbackSummary: { yes: number; no: number; notes: number };
}

export interface AnnouncementFeedback {
  id: number;
  vote: AnnouncementVote;
  note: string;
  updatedAt: string;
  userId: number;
  firstName: string;
  lastName: string;
  organizationId: number;
  organizationName: string;
}

export interface WhatsNewScreen {
  path: string;
  label: string;
  menuPath: string;
}

export const ANNOUNCEMENT_INCOMPLETE_CODE = 300012029;

export const ANNOUNCEMENT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export const ANNOUNCEMENT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export const ANNOUNCEMENT_CATEGORIES: { value: AnnouncementCategory; label: string }[] = [
  { value: 'NewFeature', label: 'Yeni özellik' },
  { value: 'Improvement', label: 'İyileştirme' },
  { value: 'Fix', label: 'Düzeltme' }
];

export const WHATS_NEW_SCREENS: WhatsNewScreen[] = [
  { path: '/search', label: 'Yeni Seyahat', menuPath: 'Münferit Seyahat › Yeni Seyahat' },
  { path: '/travels', label: 'Seyahat Yönetimi', menuPath: 'Münferit Seyahat › Seyahat Yönetimi' },
  { path: '/bizirequests/list', label: 'Taleplerim', menuPath: 'Münferit Seyahat › Taleplerim' },
  { path: '/inbox', label: 'Onay Bekleyenler', menuPath: 'Münferit Seyahat › Onay Bekleyenler' },
  { path: '/reports/travels', label: 'Seyahat Raporları', menuPath: 'Münferit Seyahat › Seyahat Raporları' },
  { path: '/agency/employees', label: 'Personel Bilgileri', menuPath: 'Acente › Yönetim › Personel Bilgileri' },
  { path: '/agency/departments', label: 'Departman İşlemleri', menuPath: 'Acente › Yönetim › Departman İşlemleri' },
  { path: '/agency/roles', label: 'Rol İşlemleri', menuPath: 'Acente › Yönetim › Rol İşlemleri' },
  { path: '/agency/company/profile', label: 'Firma Bilgileri', menuPath: 'Acente › Yönetim › Firma Bilgileri' },
  { path: '/agency/pricing', label: 'Uçak Fiyatlandırmaları', menuPath: 'Acente › Servis Bedeli ve Markup › Uçak Fiyatlandırmaları' },
  { path: '/agency/hotel-pricing', label: 'Otel Fiyatlandırmaları', menuPath: 'Acente › Servis Bedeli ve Markup › Otel Fiyatlandırmaları' },
  {
    path: '/agency/renting-pricing',
    label: 'Rent a Car Fiyatlandırmaları',
    menuPath: 'Acente › Servis Bedeli ve Markup › Rent a Car Fiyatlandırmaları'
  },
  { path: '/agency/crm/lead', label: 'Müşteri Adayları', menuPath: 'Acente › CRM › Müşteri Adayları' },
  { path: '/agency/crm/deal', label: 'Teklifler', menuPath: 'Acente › CRM › Teklifler' },
  { path: '/agency/crm/contact', label: 'Bağlantılar', menuPath: 'Acente › CRM › Bağlantılar' },
  { path: '/agency/crm/parameter', label: 'Ayarlar', menuPath: 'Acente › CRM › Ayarlar' },
  { path: '/agency/bizihotel/reservations', label: 'Rezervasyonlar', menuPath: 'Acente › Kontratlar ve Entegrasyonlar › Rezervasyonlar' },
  { path: '/reports/others', label: 'BiziHotel Raporları', menuPath: 'Acente › Kontratlar ve Entegrasyonlar › BiziHotel Raporları' },
  { path: '/agency/bizihotel/hotels', label: 'Oteller', menuPath: 'Acente › Kontratlar ve Entegrasyonlar › Oteller' },
  {
    path: '/agency/bizihotel/offers',
    label: 'Oda Ücretleri (Bizihotel)',
    menuPath: 'Acente › Kontratlar ve Entegrasyonlar › Oda Ücretleri (Bizihotel)'
  },
  {
    path: '/agency/bizihotel/import-hotels',
    label: 'Toplu Otel Girişi',
    menuPath: 'Acente › Kontratlar ve Entegrasyonlar › Toplu Otel Girişi'
  },
  {
    path: '/agency/bizihotel/priorities',
    label: 'Teklif Öncelikleri',
    menuPath: 'Acente › Kontratlar ve Entegrasyonlar › Teklif Öncelikleri'
  },
  { path: '/agency/bizihotel/mail-order', label: 'Mail Order', menuPath: 'Acente › Kontratlar ve Entegrasyonlar › Mail Order' },
  { path: '/agency/hotelrunner', label: 'HotelRunner', menuPath: 'Acente › Kontratlar ve Entegrasyonlar › HotelRunner' },
  { path: '/agency/guests', label: 'Misafir İşlemleri', menuPath: 'Acente › Misafir İşlemleri' },
  { path: '/agency/request-management/requests', label: 'Offline İşlemler', menuPath: 'Acente › Offline İşlemler' }
];

export function findScreen(path: string): WhatsNewScreen {
  return WHATS_NEW_SCREENS.find(screen => screen.path === path) || null;
}

export function categoryLabel(category: AnnouncementCategory): string {
  const match = ANNOUNCEMENT_CATEGORIES.find(item => item.value === category);
  return match ? match.label : category;
}

export function defaultButtonText(targetPath: string): string {
  const screen = findScreen(targetPath);
  return screen ? `${screen.label} ekranına git` : '';
}

export function formatAnnouncementDate(date: string): string {
  if (!date) {
    return '';
  }
  return new Date(`${date}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatTimestampDate(timestamp: string): string {
  return timestamp ? new Date(timestamp).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

export function toParagraphs(text: string): string[] {
  return (text || '').split(/\n+/).map(part => part.trim()).filter(part => part.length > 0);
}

export function isBlank(value: string): boolean {
  return value === null || value === undefined || String(value).trim() === '';
}

export function localToday(): string {
  const now = new Date();
  const month = `0${now.getMonth() + 1}`.slice(-2);
  const day = `0${now.getDate()}`.slice(-2);
  return `${now.getFullYear()}-${month}-${day}`;
}



// WEBPACK FOOTER //
// /var/www/bizisystem-frontend/src/src/app/main/pages/whats-new/whats-new.ts